import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { tokens } from "@/art/tokens";

export interface WeeklyDeltaBurstEntry {
    id: string;
    label: string;
    value: number;
    accent: string;
}

interface WeeklyDeltaBurstProps {
    entries: WeeklyDeltaBurstEntry[];
    onDone: () => void;
}

/**
 * 주차 확정 직후의 "변화가 몸으로 보이는" 짧은 플로팅 레이어.
 *
 * <p>우마무스메/풍화설월 식으로 +N 숫자가 잠깐 떠오르며 시선을 훔친 뒤 사라진다.
 * 실제 스탯 패널로 날아가 꽂히는 완전한 경로 애니메이션 전 단계로, 현재는 중앙에서
 * 좌측 HUD 방향으로 흩어지는 식으로만 처리한다.
 */
export function WeeklyDeltaBurst({ entries, onDone }: WeeklyDeltaBurstProps) {
    useEffect(() => {
        if (entries.length === 0) return;
        // 2026-04-23 ⑥: 사용자 결정 — 확정 → 델타 → 임계 → 결산 순서화에 맞춰
        // burst 길이를 900ms 로 단축. 전체 피드백 체인의 타이밍 기준 축.
        const handle = window.setTimeout(onDone, 900);
        return () => window.clearTimeout(handle);
    }, [entries, onDone]);

    return (
        <AnimatePresence>
            {entries.length > 0 && (
                <div
                    className="pointer-events-none fixed inset-0"
                    style={{ zIndex: tokens.elevation.overlay + 2 }}
                >
                    <motion.div
                        aria-hidden
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 0.45, 0] }}
                        transition={{ duration: 1.2, ease: tokens.easing.decelerate }}
                        className="absolute left-[180px] top-[212px] h-[220px] w-[220px] rounded-full"
                        style={{
                            background: `radial-gradient(circle, ${tokens.color.brass}18 0%, ${tokens.color.brass}08 36%, transparent 72%)`,
                            filter: "blur(6px)",
                        }}
                    />
                    {entries.map((entry, index) => {
                        const row = Math.floor(index / 2);
                        const col = index % 2;
                        const startX = col === 0 ? -100 : 60;
                        const midX = col === 0 ? -164 : -34;
                        const endX = col === 0 ? -520 : -420;
                        const startY = row * 18;
                        const midY = startY - 28;
                        const endY = -208 - row * 60;
                        const isGain = entry.value > 0;

                        return (
                            <motion.div
                                key={entry.id}
                                initial={{ opacity: 0, x: startX, y: startY, scale: 0.88 }}
                                animate={{
                                    opacity: [0, 1, 1, 0],
                                    x: [startX, midX, endX],
                                    y: [startY, midY, endY],
                                    scale: [0.88, 1.08, 0.72],
                                }}
                                exit={{ opacity: 0 }}
                                transition={{
                                    duration: 1.5,
                                    delay: index * 0.08,
                                    ease: tokens.easing.decelerate,
                                    times: [0, 0.22, 1],
                                }}
                                className="absolute left-1/2 top-[58%] rounded-pill px-m py-s"
                                style={{
                                    background: `linear-gradient(180deg, ${tokens.color.ink}EE, ${tokens.color.ink}C8)`,
                                    border: `1px solid ${entry.accent}66`,
                                    color: tokens.color.parchment,
                                    boxShadow: `0 0 18px ${entry.accent}44`,
                                    minWidth: 148,
                                    transformOrigin: "center center",
                                }}
                            >
                                <div
                                    className="font-display text-center"
                                    style={{
                                        fontSize: tokens.type.size.tiny,
                                        letterSpacing: tokens.type.letterSpacing.wide,
                                        color: entry.accent,
                                    }}
                                >
                                    {entry.label}
                                </div>
                                <div
                                    className="mt-[2px] text-center font-stat"
                                    style={{
                                        fontSize: tokens.type.size.body,
                                        color: isGain ? entry.accent : tokens.color.parchment,
                                        textShadow: `0 0 10px ${entry.accent}55`,
                                    }}
                                >
                                    {entry.value > 0 ? `+${entry.value}` : entry.value}
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </AnimatePresence>
    );
}
